import type { DiscountType } from '@billflow/shared';

export interface LineItemInput {
  qty: number;
  unit_price: number; // cents
  discount_type: DiscountType | null;
  discount_value: number;
}

export interface LineTotals {
  gross: number; // cents
  discount_amount: number; // cents
  line_total: number; // cents
}

// Mirror money.ts computeLineDiscount exactly
export function computeLineDiscount(
  lineTotal: number,
  discountType: DiscountType | null,
  discountValue: number,
): number {
  if (!discountType || discountValue <= 0) return 0;
  // A discount can never exceed the base it applies to (a >100% pct would
  // otherwise drive the line total negative).
  if (discountType === 'pct')
    return Math.min(Math.round((lineTotal * discountValue) / 100), lineTotal);
  return Math.min(discountValue, lineTotal); // fixed cannot exceed line total
}

export function computeInvoiceDiscount(
  subtotalAfterLineDiscounts: number,
  discountType: DiscountType | null,
  discountValue: number,
): number {
  if (!discountType || discountValue <= 0) return 0;
  if (discountType === 'pct')
    return Math.min(
      Math.round((subtotalAfterLineDiscounts * discountValue) / 100),
      subtotalAfterLineDiscounts,
    );
  return Math.min(discountValue, subtotalAfterLineDiscounts);
}

export function computeLineTotal(item: LineItemInput): LineTotals {
  // Clamp negatives so a mistyped qty/price never renders a negative live total
  // (the server rejects qty <= 0 on save).
  const gross = Math.round(Math.max(0, item.qty) * Math.max(0, item.unit_price));
  const discount_amount = computeLineDiscount(gross, item.discount_type, item.discount_value);
  return { gross, discount_amount, line_total: gross - discount_amount };
}

export interface EstimateTotals {
  subtotal: number;
  line_discount_total: number;
  subtotal_after_lines: number;
  invoice_discount_amount: number;
  total: number;
  lineTotals: LineTotals[];
}

export function computeEstimateTotals(
  items: LineItemInput[],
  invoice_discount_type: DiscountType | null,
  invoice_discount_value: number,
): EstimateTotals {
  const lineTotals = items.map(computeLineTotal);
  const subtotal = lineTotals.reduce((s, lt) => s + lt.gross, 0);
  const line_discount_total = lineTotals.reduce((s, lt) => s + lt.discount_amount, 0);
  const subtotal_after_lines = subtotal - line_discount_total;
  const invoice_discount_amount = computeInvoiceDiscount(
    subtotal_after_lines,
    invoice_discount_type,
    invoice_discount_value,
  );
  const total = subtotal_after_lines - invoice_discount_amount;
  return {
    subtotal,
    line_discount_total,
    subtotal_after_lines,
    invoice_discount_amount,
    total,
    lineTotals,
  };
}
